import { useCallback } from 'react'
import { useStore, useGridUndoStack, useGridRedoStack, useGridUndoBusy } from '../store/index.js'
import {
  beginGridChangeInWindow, closeGridChange, describeGridStep, gridStepWindow, revertGridStep,
} from '../lib/gridUndo.js'
import type { GridUndoEntry } from '../store/slices/undoSlice.js'

export interface GridUndoHandle {
  /** Dernière action annulable, `null` si la pile est vide. */
  entry:    GridUndoEntry | null
  /** Dernière annulation rétablissable, `null` s'il n'y en a pas. */
  redoEntry: GridUndoEntry | null
  canUndo:  boolean
  canRedo:  boolean
  busy:     boolean
  undo:     () => Promise<void>
  redo:     () => Promise<void>
}

/**
 * Annulation et rétablissement de la dernière action de grille.
 *
 * Les deux sens sont le même geste : rejouer une différence à l'envers. Le
 * rétablissement n'est pas l'action d'origine relancée — c'est l'annulation de
 * l'annulation.
 *
 * Ce détour a une raison. Remettre en grille un élément supprimé passe par une
 * création : le serveur lui rend sa place, pas son identifiant. La différence
 * d'origine parle donc d'éléments qui n'existent plus dès la première
 * annulation, et la rejouer à l'endroit viserait des identifiants morts. En
 * photographiant l'annulation elle-même, on obtient une différence dont les
 * deux bords sont vivants — rejouable à son tour, autant de fois qu'on
 * fait l'aller-retour.
 *
 * Chaque sens passe par une confirmation qui énonce ce qui va se produire : sur
 * une grille d'antenne, « annuler » peut vouloir dire retirer quarante éléments
 * posés par un modèle, et l'opérateur doit lire ce chiffre avant de valider.
 */
export function useGridUndo(): GridUndoHandle {
  const undoStack = useGridUndoStack()
  const redoStack = useGridRedoStack()
  const busy      = useGridUndoBusy()

  const entry     = undoStack.length > 0 ? undoStack[undoStack.length - 1] : null
  const redoEntry = redoStack.length > 0 ? redoStack[redoStack.length - 1] : null

  const replay = useCallback(async (direction: 'undo' | 'redo') => {
    const store  = useStore.getState()
    const stack  = direction === 'undo' ? store.gridUndoStack : store.gridRedoStack
    const target = stack[stack.length - 1]
    if (!target || store.gridUndoBusy) return

    const verb = direction === 'undo' ? 'Annuler' : 'Rétablir'
    if (!window.confirm(`${verb} « ${target.label} » ?\n\n${describeGridStep(target.step)}.`)) return

    store.setGridUndoBusy(true)

    // La photo qui encadre le rejeu : son inverse alimentera la pile d'en face.
    // Elle peut échouer — le rejeu se fera quand même, sans retour possible,
    // exactement comme une action dont la photo « avant » a raté.
    const recording = await beginGridChangeInWindow(
      target.label, target.date, gridStepWindow(target.step, target.date),
    )

    let report
    try {
      report = await revertGridStep(target.step)
    } catch (cause: any) {
      store.setGridUndoBusy(false)
      window.alert(cause?.message ?? `Impossible de ${verb.toLowerCase()} cette action.`)
      return
    }

    const inverse = await closeGridChange(recording)
    store.setGridUndoBusy(false)

    // Le rejeu peut porter sur un autre jour que celui affiché — une
    // rediffusion qui débordait sur la nuit suivante, un modèle sur la semaine.
    // On ouvre la journée concernée : sans cela, le compte rendu parlerait
    // d'éléments que l'écran ne montre pas.
    if (store.agendaView.date !== target.date) store.setAgendaDate(target.date)
    store.incrementGridUpdateSeq()
    store.incrementGridsUpdateSeq()

    const done  = report.deleted + report.restored + report.reverted
    const notes: string[] = []
    if (report.skipped > 0) notes.push(`${report.skipped} conservé(s) — à l’antenne, diffusé(s) ou non recréable(s)`)
    if (report.failed > 0)  notes.push(`${report.failed} refusé(s) par le serveur`)

    // Le sens inverse ne s'ouvre qu'après un rejeu complet. À moitié fait, la
    // grille n'est plus dans aucun des deux états connus : la seule sortie
    // honnête est de laisser l'opérateur retenter le même sens, une fois levé
    // ce qui a bloqué — un élément passé à l'antenne entre-temps, par exemple.
    if (report.failed === 0) {
      if (direction === 'undo') {
        store.dropGridUndoEntry(target.id)
        if (inverse) store.pushGridRedoEntry({ label: target.label, date: target.date, step: inverse })
      } else {
        store.dropGridRedoEntry(target.id)
        if (inverse) store.restoreGridUndoEntry({ label: target.label, date: target.date, step: inverse })
      }
    }

    const past = direction === 'undo' ? 'annulée' : 'rétablie'
    store.pushToast(
      notes.length > 0
        ? `« ${target.label} » ${past} · ${done} élément(s) rétabli(s) · ${notes.join(' · ')}`
        : `« ${target.label} » ${past} · ${done} élément(s) rétabli(s)`,
      notes.length > 0 ? 'warning' : 'success',
      notes.length > 0 ? 8000 : 4500,
    )
  }, [])

  const undo = useCallback(() => replay('undo'), [replay])
  const redo = useCallback(() => replay('redo'), [replay])

  return {
    entry,
    redoEntry,
    canUndo: entry !== null && !busy,
    canRedo: redoEntry !== null && !busy,
    busy,
    undo,
    redo,
  }
}
